import {
  Recommendation as UiRecommendation,
  RecommendationCategory,
  CloudProvider,
  RiskTier,
  RecommendationStatus,
} from '../types/recommendation';

/**
 * Shape actually returned today by GET /api/v1/recommendations
 * (optimization-api/src/services/recommendations/*.service.ts). Thinner
 * than the richer UI type below - see Docs/ANOMALY-RECOMMENDATIONS-RCA-
 * GAP-CLOSURE-PLAN.md Part 2, Req 30 (Unified Recommendation Schema).
 */
export interface ApiRecommendation {
  id: string;
  type: string;
  resource_id: string;
  resource_name?: string;
  provider: string;
  service: string;
  current_cost: number;
  potential_savings: number;
  reason: string;
  recommendation: string;
  confidence: string; // 'high' | 'medium' | 'low'
  priority: string; // 'high' | 'medium' | 'low'
  metadata?: Record<string, any>;
  created_at: string;
  status?: string;
}

const TYPE_LABELS: Record<string, string> = {
  idle_resource: 'Idle Resource',
  rightsizing: 'Rightsizing',
  orphaned_resource: 'Orphaned Resource',
  old_snapshot: 'Old Snapshot',
  architecture_migration: 'Architecture Migration',
  licensing_optimization: 'Licensing Optimization',
  nonprod_premium_waste: 'Non-Prod Premium Waste',
  scheduled_use: 'Scheduled Use',
  commitment_optimization: 'Commitment Optimization',
  storage_optimization: 'Storage & Logs',
  modernization: 'Modernization',
  network_optimization: 'Network & Data Transfer',
  nonprod_optimization: 'Non-Prod Optimization',
  ai_optimization: 'AI / GenAI Spend',
  pricing_optimization: 'Pricing & Licensing',
};

// Mirrors optimization-api's ACTION_VERB_BY_RECOMMENDATION_TYPE
// (l1-dry-run.service.ts) - the platform's own real classification of how
// safe/automatable each finding type is. Reused here as an honest risk
// tier instead of inventing a per-resource ML risk score that doesn't
// exist anywhere in the backend.
const RISK_TIER_BY_TYPE: Record<string, RiskTier> = {
  idle_resource: RiskTier.LOW, // stop - reversible
  orphaned_resource: RiskTier.LOW, // delete_orphan - unowned, low blast radius
  old_snapshot: RiskTier.LOW, // delete_orphan
  rightsizing: RiskTier.MEDIUM, // resize - real config change
  architecture_migration: RiskTier.MEDIUM, // resize
  licensing_optimization: RiskTier.MEDIUM, // tag_fix - flag for human review
  nonprod_premium_waste: RiskTier.MEDIUM, // tag_fix
  scheduled_use: RiskTier.MEDIUM, // tag_fix
  commitment_optimization: RiskTier.HIGH, // purchase_commitment - financial commitment
  // Billing-line detectors: all tag_fix (owner review first).
  storage_optimization: RiskTier.MEDIUM,
  modernization: RiskTier.MEDIUM,
  network_optimization: RiskTier.MEDIUM,
  nonprod_optimization: RiskTier.MEDIUM,
  ai_optimization: RiskTier.MEDIUM,
  pricing_optimization: RiskTier.MEDIUM,
};

// Confidence is a real, if coarse, signal already computed by every
// detector (utilization_metrics detection = high, name/cost-pattern
// heuristic = medium) - quantized here for the UI's 0-1 scale, not
// invented from nothing.
const CONFIDENCE_VALUE: Record<string, number> = {
  high: 0.85,
  medium: 0.55,
  low: 0.25,
};

function mapCategory(type: string): RecommendationCategory {
  return (Object.values(RecommendationCategory) as string[]).includes(type)
    ? (type as RecommendationCategory)
    : RecommendationCategory.OTHER;
}

/**
 * The API always returns one of the 12 states (legacy values are normalised
 * server-side). An unexpected value is passed through as-is rather than
 * silently shown as Open - that silent fallback to "Pending" was gap 15.
 */
function mapStatus(status?: string): RecommendationStatus {
  if (!status) return RecommendationStatus.OPEN;
  return status as RecommendationStatus;
}

/**
 * Converts the real, deployed API response into the richer UI type used by
 * RecommendationDetailModal/RecommendationCard/RecommendationFilters -
 * populating only fields the backend genuinely computes. Fields with no
 * real backing anywhere (calculationDetail, rawSignals, evidenceChain,
 * explanation, accountId, region) are left undefined so the modal's
 * existing conditional rendering hides those sections instead of showing
 * fabricated content.
 */
export function adaptApiRecommendation(api: ApiRecommendation): UiRecommendation {
  const metadata = api.metadata || {};
  const utilization = metadata.utilization_metrics;

  return {
    id: api.id,
    category: mapCategory(api.type),
    cloudProvider: (api.provider as CloudProvider) || CloudProvider.AWS,
    title: `${TYPE_LABELS[api.type] || api.type}: ${api.resource_name || api.resource_id}`,
    description: api.recommendation || api.reason,

    estimatedMonthlySavings: api.potential_savings,
    confidence: CONFIDENCE_VALUE[api.confidence] ?? 0.5,
    riskScore: 0, // no per-resource risk model exists - see riskTier for the real, honest classification
    priority:
      api.potential_savings *
      (CONFIDENCE_VALUE[api.confidence] ?? 0.5) *
      (1 - (RISK_TIER_BY_TYPE[api.type] === RiskTier.HIGH ? 0.7 : RISK_TIER_BY_TYPE[api.type] === RiskTier.MEDIUM ? 0.4 : 0.1)),

    riskTier: RISK_TIER_BY_TYPE[api.type] || RiskTier.MEDIUM,
    status: mapStatus(api.status),

    targetResourceId: api.resource_id,
    targetResourceName: api.resource_name || api.resource_id,
    targetResourceType: api.service,
    accountId: '',
    region: metadata.region || '',

    // Real numbers when the detector actually ran a utilization check
    // (see rightsizing.service.ts/idle-resource.service.ts's
    // `utilization_metrics` metadata field); otherwise left as {} so the
    // modal's config panels render empty rather than crashing on undefined.
    currentConfig: {},
    proposedConfig: metadata.target_size ? { targetSize: metadata.target_size } : {},
    utilizationMetrics: utilization
      ? {
          cpuAvg: utilization.cpu_avg,
          cpuP95: utilization.cpu_p95,
          memoryAvg: utilization.memory_avg ?? 0,
          memoryP95: utilization.memory_p95 ?? 0,
        }
      : undefined,

    createdAt: api.created_at,
    updatedAt: api.created_at,
    createdBy: 'system',
  };
}
